
import type { PropType } from 'vue'
import { computed, defineComponent, onMounted, provide, ref, watch } from 'vue'
import { GET_PROPS_KEYS, ITEMS_TO_SHOW, UPDATE_CONFIG_KEYS } from './consts'

export interface VirtualScrollerConfig {
  startNode: number
  visibleNodes: number
}

const OFFSET_VISIBLE_NODES = 5

export default defineComponent({
  name: 'EpbVirtualScroller',
  props: {
    items: {
      type: Array as PropType<Array<unknown>>,
      required: true,
    },
    rowHeight: {
      type: Number,
      default: 60,
    },
    itemsPerRow: {
      type: Number,
      default: 1,
    },
  },
  setup(props) {
    const virtualScroller = ref<HTMLElement>(null)

    const config = ref<VirtualScrollerConfig>({
      startNode: 0,
      visibleNodes: 0,
    })

    const scrollerStyles = computed(() => {
      return {
        minHeight: `${(props.items.length / props.itemsPerRow) * props.rowHeight}px`,
      }
    })

    const listStyles = computed(() => {
      return {
        transform: `translateY(${config.value.startNode * props.rowHeight}px)`,
      }
    })

    const itemsToShow = computed(() => {
      const start = config.value.startNode * props.itemsPerRow
      const end = start + config.value.visibleNodes
      const remaining = props.items.length - end
      return props.items.slice(start, remaining < OFFSET_VISIBLE_NODES ? props.items.length : end)
    })

    const getConfig = (element: HTMLElement): VirtualScrollerConfig => {
      const startNode = Math.max(0, Math.floor(element.scrollTop / props.rowHeight))
      const visibleNodes = Math.ceil(element.offsetHeight / props.rowHeight)

      return {
        startNode,
        visibleNodes: visibleNodes * props.itemsPerRow + OFFSET_VISIBLE_NODES,
      }
    }

    const updateConfig = (element: HTMLElement): void => {
      config.value = getConfig(element)
    }

    const onScroll = (event): void => {
      const element = event.target as HTMLElement

      updateConfig(element)
    }

    onMounted(() => {
      updateConfig(virtualScroller.value)
    })

    watch(
      () => [props.items, props.itemsPerRow, props.rowHeight],
      () => {
        updateConfig(virtualScroller.value)
      },
    )

    provide(GET_PROPS_KEYS, () => props)
    provide(ITEMS_TO_SHOW, itemsToShow)
    provide(UPDATE_CONFIG_KEYS, () => updateConfig(virtualScroller.value))

    return {
      baseClass: 'epb-virtual-scroller',
      scrollerStyles,
      listStyles,
      virtualScroller,
      itemsToShow,
      config,
      onScroll,
    }
  },
})
